import type { RouterOutputs } from "@/trpc";
import type { VerificationPreparation } from "./types";

export type LicenseSuggestion =
  RouterOutputs["psv"]["confirmVerificationNpi"]["licenseSuggestions"][number];
export type LicenseDraft = {
  key: string;
  credentialId?: string;
  number: string;
  stateCode: string;
};

export function createLicenseDrafts(
  preparation: VerificationPreparation,
): LicenseDraft[] {
  if (!preparation.licenses.length) {
    return [{ key: "new-license", number: "", stateCode: "" }];
  }
  return preparation.licenses.map((license) => ({
    key: license.credentialId,
    credentialId: license.credentialId,
    number: license.number ?? "",
    stateCode: license.stateCode ?? "",
  }));
}

export function changedLicenses({
  preparation,
  licenses,
}: {
  preparation: VerificationPreparation;
  licenses: LicenseDraft[];
}) {
  return licenses.filter((license) => {
    const saved = preparation.licenses.find(
      (item) => item.credentialId === license.credentialId,
    );
    return (
      !saved ||
      license.number.trim() !== (saved.number?.trim() ?? "") ||
      license.stateCode !== (saved.stateCode ?? "")
    );
  });
}

export function hasNewDuplicateStates(licenses: LicenseDraft[]) {
  return licenses.some(
    (license, index) =>
      license.stateCode &&
      licenses.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.stateCode === license.stateCode &&
          (!license.credentialId || !other.credentialId),
      ),
  );
}

/** Existing values, including staff drafts, win. Alternatives remain visible
 * beside their state's row; multiple new numbers need an explicit choice. */
export function mergeLicenseSuggestions({
  licenses,
  suggestions,
}: {
  licenses: LicenseDraft[];
  suggestions: LicenseSuggestion[];
}): LicenseDraft[] {
  const groups = new Map<string, string[]>();
  for (const suggestion of suggestions) {
    const numbers = groups.get(suggestion.stateCode) ?? [];
    if (!numbers.includes(suggestion.number)) {
      numbers.push(suggestion.number);
    }
    groups.set(suggestion.stateCode, numbers);
  }
  const additions: LicenseDraft[] = [];
  for (const [stateCode, numbers] of groups) {
    if (licenses.some((license) => license.stateCode === stateCode)) {
      continue;
    }
    additions.push({
      key: crypto.randomUUID(),
      stateCode,
      number: numbers.length === 1 ? numbers[0] : "",
    });
  }
  if (!additions.length) {
    return licenses;
  }
  const retained = licenses.filter(
    (license) =>
      license.credentialId || license.stateCode || license.number.trim(),
  );
  return [...retained, ...additions];
}

export function savedFactsMatch({
  preparation,
  npi,
  providerName,
  licenses,
}: {
  preparation: VerificationPreparation;
  npi: string | null;
  providerName: string;
  licenses: LicenseDraft[];
}) {
  const facts = (
    values: { stateCode: string | null; number: string | null }[],
  ) =>
    values
      .map(
        (license) =>
          `${license.stateCode ?? ""}:${license.number?.trim() ?? ""}`,
      )
      .sort();
  return (
    preparation.npi === npi &&
    preparation.providerName === providerName &&
    JSON.stringify(facts(preparation.licenses)) ===
      JSON.stringify(facts(licenses))
  );
}

export function isLicenseIncluded({
  license,
  skippedStateCodes,
}: {
  license: LicenseDraft;
  skippedStateCodes: string[];
}) {
  return !skippedStateCodes.includes(license.stateCode);
}

/** Issues on skipped licenses must not block a run that never checks them. */
export function issuesForIncludedLicenses<T extends { credentialId: string }>({
  issues,
  licenses,
  skippedStateCodes,
}: {
  issues: T[];
  licenses: LicenseDraft[];
  skippedStateCodes: string[];
}) {
  return issues.filter((issue) =>
    licenses.some(
      (license) =>
        license.credentialId === issue.credentialId &&
        isLicenseIncluded({ license, skippedStateCodes }),
    ),
  );
}
